<template>
  <div>
    <vxe-card>
      <template #title>
        <vxe-select v-model="query['owner.id']" :options="owners" placeholder="placeholder.owner" @change="refresh"/>
        <vxe-input v-model="query['item.no']" placeholder="placeholder.itemNo" @clear="refresh" @keydown.native.enter="refresh"/>
        <vxe-input v-model="query['storageRoom.name']" placeholder="placeholder.storageRoom" @clear="refresh" @click="onStorageRoomSelect" @keydown.native.enter="refresh"/>
        <vxe-input v-model="query['zone.name']" placeholder="placeholder.zone" @clear="refresh" @click="onZoneSelect" @keydown.native.enter="refresh"/>
        <vxe-input v-model="query['location.no']" placeholder="placeholder.location" @clear="refresh" @click="onLocationSelect" @keydown.native.enter="refresh"/>
        <vxe-input v-model="query['operator']" placeholder="placeholder.inventoryClerk" @clear="refresh" @click="onOperatorSelect" @keydown.native.enter="refresh"/>
        <vxe-select v-model="query.changed" :options="countingTypes" placeholder="placeholder.pleaseSelectCountingType" @change="refresh"/>
        <vxe-date-range-picker v-model="query.range" clearable linked-panels placeholder="business.label.addTime" type="date" @change="refresh"/>
      </template>
      <template #extra>
        <vxe-button content="button.query" icon="vxe-icon-search" status="primary" @click="refresh"/>
        <vxe-button content="button.reset" icon="vxe-icon-undo" status="danger" @click="reset"/>
        <vxe-button content="button.print" icon="vxe-icon-print" status="warning" @click="print"/>
      </template>
      <vxe-table ref="table" :data="data" :loading="loading">
        <vxe-column fixed="left" type="seq" width="40"/>
        <vxe-column field="item.no" title="business.label.itemNo" width="80"/>
        <vxe-column field="item.name" title="business.label.itemName"/>
        <vxe-column field="owner.name" title="business.label.ownerName" width="120"/>
        <vxe-column field="pack.specification" title="business.label.specification"/>
        <vxe-column field="pack.factor" title="business.label.factor" width="60"/>
        <vxe-column field="lot.no" title="business.label.lot"/>
        <vxe-column field="batch.no" title="business.label.batchNo"/>
        <vxe-column field="location.no" title="business.label.location"/>
        <vxe-column field="inventory.availableQuantity" title="business.label.availableQuantity"/>
        <vxe-column field="inventory.quantity" title="business.label.quantity"/>
        <vxe-column :cell-render="{name:'FormatSelect',options:inventoryStates}" field="inventory.inventoryState" title="business.label.inventoryStatus"/>
      </vxe-table>
      <template #footer>
        <vxe-pager :current-page="page" :loading="loading" :page-size="size" :total="contentSize" @page-change="pageChange"/>
      </template>
    </vxe-card>
    <storage-room-modal ref="storageRoomModal" @change="onStorageRoomChange"/>
    <zone-modal ref="zoneModal" @change="onZoneChange"/>
    <location-modal ref="locationModal" @change="onLocationChange"/>
    <user-modal ref="userModal" @change="onOperatorChange"/>
  </div>
</template>

<script>
import { findAll } from '@/api/Api'
import StorageRoomModal from '@/views/modal/StorageRoomModal.vue'
import ZoneModal from '@/views/modal/ZoneModal.vue'
import LocationModal from '@/views/modal/LocationModel.vue'
import UserModal from '@/views/modal/UserModal.vue'
import { getEnums, getOptions } from '@/utils/util'
import moment from 'moment/moment'

export default {
  name: 'CountingPlan',
  components: { UserModal, LocationModal, ZoneModal, StorageRoomModal },
  data() {
    return {
      loading: false,
      data: [],
      query: {
        'owner.id': undefined,
        'item.no': null,
        'storageRoom.id': null,
        'storageRoom.name': null,
        'zone.id': null,
        'zone.name': null,
        'changed': undefined,
        'location.no': null,
        operator: null,
        range: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
      },
      owners: [],
      inventoryStates: [],
      countingTypes: [
        {
          value: true,
          label: this.$t('option.outboundCounting')
        },
        {
          value: false,
          label: this.$t('option.notOutboundCounting')
        }
      ],
      page: 1,
      size: 20,
      contentSize: 0
    }
  },
  created() {
    findAll('partyFactory', { available: true, 'type.ordinal': 1 }).then(res => {
      if (res.success) {
        this.owners = getOptions(res.result)
      }
      return getEnums('InventoryState')
    }).then(result => {
      this.inventoryStates = result
    })
  },
  activated() {
    this.refresh()
  },
  methods: {
    pageChange({ currentPage, pageSize }) {
      this.page = currentPage
      this.size = pageSize
      this.fetch()
    },
    onStorageRoomSelect() {
      this.$refs.storageRoomModal.open()
    },
    onStorageRoomChange(values) {
      if (values.length !== 0) {
        let sr = values[0]
        this.query['storageRoom.id'] = sr.id
        this.query['storageRoom.name'] = sr.name
        this.query['zone.id'] = null
        this.query['zone.name'] = null
        this.query['location.no'] = null
      } else {
        this.query['storageRoom.id'] = null
        this.query['storageRoom.name'] = null
        this.query['zone.id'] = null
        this.query['zone.name'] = null
        this.query['location.no'] = null
      }
    },
    onZoneSelect() {
      this.$refs.zoneModal.open({ 'storageRoom.id': this.query['storageRoom.id'] })
    },
    onZoneChange(values) {
      if (values.length !== 0) {
        let zone = values[0]
        this.query['zone.id'] = zone.id
        this.query['zone.name'] = zone.name
        this.query['location.no'] = null
      } else {
        this.query['zone.id'] = null
        this.query['zone.name'] = null
        this.query['location.no'] = null
      }
    },
    onLocationSelect() {
      this.$refs.locationModal.open({ 'storageRoom.id': this.query['storageRoom.id'], 'zone.id': this.query['zone.id'] })
    },
    onLocationChange(values) {
      if (values.length !== 0) {
        let location = values[0]
        this.query['location.no'] = location.no
      } else {
        this.query['location.no'] = null
      }
    },
    onOperatorSelect() {
      this.$refs.userModal.open({ available: true })
    },
    onOperatorChange(values) {
      if (values.length !== 0) {
        let operator = values[0]
        this.query['operator'] = operator.name
      } else {
        this.query['operator'] = null
      }
    },
    init() {
      this.query = {
        'owner.id': undefined,
        'item.no': null,
        'storageRoom.id': null,
        'storageRoom.name': null,
        'zone.id': null,
        'zone.name': null,
        'changed': undefined,
        'location.no': null,
        'operator': null,
        range: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
      }
      this.data = []
      this.refresh()
    },
    fetch() {
      this.loading = true
      const args = Object.assign({}, this.query)
      if (args.range.length > 0) {
        args['dateFilter'] = { start: args.range[0], end: args.range[1] }
      } else {
        args['dateFilter'] = null
      }
      findAll('countingInventoryPlanningFactory', args, this.page, this.size).then(res => {
        if (res.success) {
          this.data = res.result
          this.contentSize = res.contentSize
        }
      }).finally(() => this.loading = false)
    },
    refresh() {
      this.page = 1
      this.fetch()
    },
    reset() {
      this.query = {
        'owner.id': undefined,
        'item.no': null,
        'storageRoom.id': null,
        'storageRoom.name': null,
        'zone.id': null,
        'zone.name': null,
        'changed': undefined,
        'location.no': null,
        'operator': null,
        range: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
      }
      this.refresh()
    },
    print() {
    }
  }
}
</script>